import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router';
import type { CurriculumSavePayload } from '@edusense/features-course';
import { TabNav } from '@edusense/shared-ui-layout';
import { Tooltip } from '@heroui/react';
import { COURSE_EDIT_TEST_IDS } from '@edusense/shared-constants';
// Lucide React 아이콘
import {
  ArrowLeft,
  Save,
  BookOpen,
  Settings,
  Layout,
  DollarSign,
  Video,
  Send,
  RotateCcw,
  Eye,
} from 'lucide-react';

// 스타일 컴포넌트 임포트
import {
  TemplateContainer,
  HeaderBar,
  HeaderLeft,
  BackButton as StyledBackButton,
  HeaderTitle,
  PageTitle,
  CourseTitleDisplay,
  HeaderRight,
  SaveStatus as StyledSaveStatus,
  SaveIcon as StyledSaveIcon,
  SaveChangesButton,
  PreviewButton,
  PublishButton as StyledPublishButton,
  ContentLayout,
  MainContent
} from './CourseEditTemplate.styles';

const LANGUAGE_LABELS: Record<string, string> = {
  ko: '한국어',
  'ko-kr': '한국어',
  en: 'English',
  'en-us': 'English (US)',
  'en-gb': 'English (UK)',
  ja: '日本語',
  zh: '中文(간체)',
  'zh-cn': '中文(간체)',
  'zh-tw': '中文(번체)',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
};

const normalizeLanguageCode = (code?: string | null): string => {
  if (!code) {
    return 'ko';
  }
  const trimmed = `${code}`.trim();
  if (!trimmed) {
    return 'ko';
  }
  return trimmed.toLowerCase();
};

const getLanguageLabel = (languageCode: string): string => {
  const normalized = normalizeLanguageCode(languageCode);
  return LANGUAGE_LABELS[normalized] ?? normalized.toUpperCase();
};

// 가능한 탭 경로를 타입으로 정의
type TabPath = 'edit' | 'curriculum' | 'pricing' | 'resources' | 'settings' | 'publish';

export interface CourseEditTemplateProps {
  children: React.ReactNode;
  title?: string;
  courseTitle?: string;
  courseId?: string;
  isSaving?: boolean;
  lastSaved?: string;
  onBack?: () => void;
  onPublish?: () => void;
  isPublishing?: boolean;
  isPublished?: boolean;
  isCreating?: boolean;
  currentPath?: TabPath;
  onSave?: () => void;
  onReset?: () => void;
  isDirty?: boolean;
  publishDisabled?: boolean;
  onPreview?: () => void;
  previewDisabled?: boolean;
  previewLanguage?: string | null;
  onCurriculumDirtyChange?: (isDirty: boolean) => void;
  requestNavigationBypass?: () => void;
}

// CurriculumTabContentHandle 타입 정의
export interface CurriculumTabContentHandle {
  getSectionsDataForSave(): CurriculumSavePayload | null;
}

// 모바일 뷰 감지용 훅
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);

  return isMobile;
}

const CourseEditTemplate = React.forwardRef<CurriculumTabContentHandle, CourseEditTemplateProps>(({
  children,
  title = '강의 편집',
  courseTitle,
  courseId = '',
  isSaving = false,
  lastSaved = '',
  onBack,
  onPublish,
  isPublishing = false,
  isPublished = false,
  isCreating: explicitIsCreating,
  currentPath = 'edit',
  onSave,
  onReset,
  isDirty = false,
  publishDisabled,
  onPreview,
  previewDisabled,
  previewLanguage,
  onCurriculumDirtyChange,
  requestNavigationBypass,
}, ref) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();

  const isCreating = explicitIsCreating !== undefined ? explicitIsCreating : (courseId === 'new');
  const displayCourseTitle = isCreating ? null : courseTitle;

  // TabNav를 위한 탭 설정
  const tabs = [
    { id: 'edit', label: '기본', icon: <Layout size={18} />, dataTestId: COURSE_EDIT_TEST_IDS.TAB_BASIC },
    { id: 'curriculum', label: '커리큘럼', icon: <BookOpen size={18} />, dataTestId: COURSE_EDIT_TEST_IDS.TAB_CURRICULUM },
    { id: 'pricing', label: '가격', icon: <DollarSign size={18} />, dataTestId: COURSE_EDIT_TEST_IDS.TAB_PRICING },
    { id: 'resources', label: '자료', icon: <Video size={18} />, dataTestId: COURSE_EDIT_TEST_IDS.TAB_RESOURCES },
    { id: 'settings', label: '설정', icon: <Settings size={18} />, dataTestId: COURSE_EDIT_TEST_IDS.TAB_SETTINGS },
    { id: 'publish', label: '게시', icon: <Send size={18} />, dataTestId: COURSE_EDIT_TEST_IDS.TAB_PUBLISH },
  ];

  const validTabIds = useMemo(() => new Set(tabs.map(tab => tab.id)), [tabs]);

  const handleTabChange = (tabId: string) => {
    if (!validTabIds.has(tabId)) {
      return;
    }

    const currentSegments = location.pathname.split('/').filter(Boolean);
    if (currentSegments.length === 0) {
      navigate({ pathname: `/${tabId}`, search: location.search, hash: location.hash });
      return;
    }

    const nextSegments = [...currentSegments];
    const lastSegment = nextSegments[nextSegments.length - 1];

    if (validTabIds.has(lastSegment)) {
      nextSegments[nextSegments.length - 1] = tabId;
    } else {
      nextSegments.push(tabId);
    }

    const nextPathname = `/${nextSegments.join('/')}`;

    if (nextPathname !== location.pathname) {
      requestNavigationBypass?.();
      navigate({ pathname: nextPathname, search: location.search, hash: location.hash });
    }
  };

  const handlePublish = () => {
    if (onPublish && courseId && courseId !== 'new') {
      onPublish();
    } else {
      console.warn("Cannot publish: courseId is 'new' or not defined.");
    }
  };

  const handlePreview = () => {
    if (!onPreview || previewDisabled) {
      return;
    }
    requestNavigationBypass?.();
    onPreview();
  };

  const getSaveStatusMessage = () => {
    if (isSaving) return '저장 중...';
    if (isDirty) return '변경사항 있음';
    if (lastSaved) {
      // 시간만 표시 (오후 3:35:49 형식)
      const timeOnly = lastSaved.split(' ').slice(-1)[0];
      return timeOnly;
    }
    return '저장됨';
  };

  const getSaveStatusTooltip = () => {
    if (isSaving) return '저장 중...';
    if (isDirty && lastSaved) return `${lastSaved} (저장되지 않은 변경사항 있음)`;
    if (isDirty) return '저장되지 않은 변경사항 있음';
    if (lastSaved) return lastSaved;
    return '모든 변경사항 저장됨';
  };

  const previewLanguageLabel = previewLanguage ? getLanguageLabel(previewLanguage) : null;

  return (
    <TemplateContainer>
      <HeaderBar>
        <HeaderLeft>
          <StyledBackButton onClick={onBack || (() => window.history.back())}>
            <ArrowLeft size={20} />
          </StyledBackButton>
          <HeaderTitle>
            <PageTitle>{isCreating ? '강의 생성' : title}</PageTitle>
            {displayCourseTitle && (
              <CourseTitleDisplay>{displayCourseTitle}</CourseTitleDisplay>
            )}
          </HeaderTitle>
        </HeaderLeft>
        <HeaderRight>
          <Tooltip
            content={getSaveStatusTooltip()}
            placement="top"
            delay={0}
            closeDelay={0}
            classNames={{
              content: '!w-auto whitespace-nowrap text-xs bg-content1 shadow-small',
            }}
          >
            <StyledSaveStatus $isSaving={isSaving} $isMobile={isMobile}>
              <StyledSaveIcon $isSaving={isSaving} size={16} />
              {!isMobile && <span>{getSaveStatusMessage()}</span>}
            </StyledSaveStatus>
          </Tooltip>

          {onReset && (
            <Tooltip
              content="변경사항 취소"
              placement="top"
              delay={0}
              closeDelay={0}
              classNames={{
                content: '!w-auto whitespace-nowrap text-xs bg-content1 shadow-small',
              }}
            >
              <SaveChangesButton
                data-testid={COURSE_EDIT_TEST_IDS.HEADER_RESET_BUTTON}
                onClick={onReset}
                disabled={!isDirty || isSaving}
                $isSaving={false}
                $isIconOnly={true}
              >
                <RotateCcw size={18} />
              </SaveChangesButton>
            </Tooltip>
          )}

          {onSave && (
            <Tooltip
              content={isSaving ? '저장 중...' : '변경사항 저장'}
              placement="top"
              delay={0}
              closeDelay={0}
              classNames={{
                content: '!w-auto whitespace-nowrap text-xs bg-content1 shadow-small',
              }}
            >
              <SaveChangesButton
                data-testid={COURSE_EDIT_TEST_IDS.HEADER_SAVE_BUTTON}
                onClick={onSave}
                disabled={!isDirty || isPublishing || isSaving}
                $isSaving={isSaving}
                $isIconOnly={true}
              >
                <Save size={18} />
              </SaveChangesButton>
            </Tooltip>
          )}

          {onPreview && !isCreating && (
            <Tooltip
              content={previewLanguageLabel ? `미리보기 (${previewLanguageLabel})` : '미리보기'}
              placement="top"
              delay={0}
              closeDelay={0}
              classNames={{
                content: '!w-auto whitespace-nowrap text-xs bg-content1 shadow-small',
              }}
            >
              <PreviewButton
                onClick={handlePreview}
                disabled={previewDisabled}
                data-testid={COURSE_EDIT_TEST_IDS.HEADER_PREVIEW_BUTTON}
                $isMobile={isMobile}
              >
                <Eye size={18} />
                {!isMobile && <span>미리보기</span>}
              </PreviewButton>
            </Tooltip>
          )}

          {!isCreating && (
            <Tooltip
              content={isPublishing ? '게시 중...' : (isPublished ? '게시됨' : '게시하기')}
              placement="top"
              delay={0}
              closeDelay={0}
              classNames={{
                content: '!w-auto whitespace-nowrap text-xs bg-content1 shadow-small',
              }}
            >
              <StyledPublishButton
                onClick={handlePublish}
                disabled={publishDisabled ?? (isPublishing || isSaving || isDirty || isPublished)}
                $isPublished={isPublished}
                $isIconOnly={true}
                data-testid={COURSE_EDIT_TEST_IDS.HEADER_PUBLISH_BUTTON}
              >
                <Send size={18} />
              </StyledPublishButton>
            </Tooltip>
          )}
        </HeaderRight>
      </HeaderBar>

      {/* TabNav를 상단에 배치 */}
      <TabNav
        activeTabId={currentPath}
        tabs={tabs}
        onTabChange={handleTabChange}
        variant="border"
        className="bg-white border-b border-gray-200/30"
      />

      <ContentLayout>
        <MainContent>
          {children}
        </MainContent>
      </ContentLayout>
    </TemplateContainer>
  );
});

CourseEditTemplate.displayName = 'CourseEditTemplate';

export default CourseEditTemplate;
